import { Injectable } from '@angular/core';
import { Observable, forkJoin, catchError, of, map, shareReplay } from 'rxjs';
import { TenantApi } from 'src/app/platform-directory-api';
import { RequestCache } from '../cache/request.cache';
import { LogHelper } from '../logging/log-helper';
import { TimeSpan } from '../model/date.helper';

export interface FieldConfigurationData {
  fieldConfigurations: string[];
  displayNames: Record<string, string>;
}

@Injectable({
  providedIn: 'root',
})
export class FieldConfigurationService {
  private readonly cache = new RequestCache<FieldConfigurationData>();
  private readonly logger = new LogHelper('FieldConfigurationService');
  private readonly cacheKey = 'field-configuration';
  private readonly cacheTtl = TimeSpan.fromMinutes(5);
  private clearTimeout: NodeJS.Timeout | null = null;
  private referenceCount = 0;

  constructor(private readonly tenantService: TenantApi) {}

  register() {
    this.referenceCount++;
    if (this.clearTimeout) {
      clearTimeout(this.clearTimeout);
      this.clearTimeout = null;
    }
  }

  /**
   * Unregisters a reference to the field configuration service
   * This will clear the cache if there are no more references after a delay
   */
  unregister() {
    this.referenceCount--;

    if (this.referenceCount > 0) {
      return;
    }

    if (this.clearTimeout) {
      clearTimeout(this.clearTimeout);
      this.clearTimeout = null;
    }

    this.clearTimeout = setTimeout(() => {
      this.clearCache();
    }, TimeSpan.fromMinutes(10));
  }

  /**
   * Gets field configuration data for a specific tenant and type, with caching
   * @param tenantId The tenant ID
   * @param type The configuration type (e.g., 'usersv2')
   * @param ttlMs Cache TTL in milliseconds (default: 5 minutes)
   */
  getFieldConfiguration(
    tenantId: string,
    type: string,
    ttlMs: number = this.cacheTtl,
  ): Observable<FieldConfigurationData> {
    const cacheKey = `${this.cacheKey}:${tenantId}:${type}`;

    return this.cache.executeRequest(
      cacheKey,
      () => this.fetchFieldConfiguration(tenantId, type).pipe(shareReplay(1)),
      (data) => data,
      {
        ttlMs,
        errorHandler: (error) => {
          this.logger.debug('Error fetching field configuration:', error);
          return of({ fieldConfigurations: [], displayNames: {} });
        },
      },
    );
  }

  /**
   * Clears the cache for a specific tenant and type, or all cache if no parameters provided
   */
  clearCache(): void {
    this.cache.clear();
  }

  private fetchFieldConfiguration(tenantId: string, type: string): Observable<FieldConfigurationData> {
    const fieldConfigurations$ = this.tenantService.tenantGetFieldConfiguration(tenantId, type);
    const displayNames$ = this.tenantService.tenantGetFieldConfigurationDisplayNames(tenantId, type).pipe(
      catchError((error) => {
        this.logger.debug('Error fetching display names:', error);
        return of({});
      }),
    );

    return forkJoin([fieldConfigurations$, displayNames$]).pipe(
      map(([fieldConfigurations, displayNames]) => ({
        fieldConfigurations,
        displayNames,
      })),
      catchError((error) => {
        this.logger.debug('Error fetching field configurations:', error);
        return of({ fieldConfigurations: [], displayNames: {} });
      }),
    );
  }
}
